import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { getUsersList, importUsers } from "../../services/users";
import {
  ROW_STATUS,
  countRowsByStatus,
  getRowsToImport,
  mergeImportReport,
  parseUserImportFile,
  validateImportRows,
} from "./importUserUtils";

const STATUS_ROW_CLASS = {
  [ROW_STATUS.READY]: "",
  [ROW_STATUS.INVALID]: "bg-[#fff5f5]",
  [ROW_STATUS.SKIPPED]: "bg-[#fffbeb]",
  [ROW_STATUS.CREATED]: "bg-[#f0fdf4]",
  [ROW_STATUS.FAILED]: "bg-[#fff5f5]",
};

const STATUS_BADGE_CLASS = {
  [ROW_STATUS.READY]: "bg-[#e6f7f7] text-[#007a79]",
  [ROW_STATUS.INVALID]: "bg-[#fde8e8] text-[#9b1c1c]",
  [ROW_STATUS.SKIPPED]: "bg-[#fef3c7] text-[#92400e]",
  [ROW_STATUS.CREATED]: "bg-[#dcfce7] text-[#166534]",
  [ROW_STATUS.FAILED]: "bg-[#fde8e8] text-[#9b1c1c]",
};

const ImportSummaryChips = ({ counts, t }) => {
  const chips = [
    {
      key: "ready",
      count: counts[ROW_STATUS.READY] || 0,
      label: t("settingsUsers.importStatusReady"),
      className: "bg-[#e6f7f7] text-[#007a79]",
    },
    {
      key: "skipped",
      count: counts[ROW_STATUS.SKIPPED] || 0,
      label: t("settingsUsers.importStatusSkipped"),
      className: "bg-[#fef3c7] text-[#92400e]",
    },
    {
      key: "invalid",
      count: counts[ROW_STATUS.INVALID] || 0,
      label: t("settingsUsers.importStatusInvalid"),
      className: "bg-[#fde8e8] text-[#9b1c1c]",
    },
    {
      key: "created",
      count: counts[ROW_STATUS.CREATED] || 0,
      label: t("settingsUsers.importStatusCreated"),
      className: "bg-[#dcfce7] text-[#166534]",
    },
    {
      key: "failed",
      count: counts[ROW_STATUS.FAILED] || 0,
      label: t("settingsUsers.importStatusFailed"),
      className: "bg-[#fde8e8] text-[#9b1c1c]",
    },
  ].filter((chip) => chip.count > 0);

  if (!chips.length) return null;

  return (
    <div className="mb-3 flex flex-wrap gap-2">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${chip.className}`}
        >
          {chip.count} {chip.label}
        </span>
      ))}
    </div>
  );
};

const ImportUsersModal = ({ isOpen, onClose, onImported, corporation }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [existingUsers, setExistingUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const googleSSORequired = !!corporation?.googleSSORequired;

  const resetState = useCallback(() => {
    setFileName("");
    setRows([]);
    setImportDone(false);
    setImportReport(null);
  }, []);

  const handleClose = () => {
    if (isImporting) return;
    resetState();
    onClose?.();
  };

  const resolveIssueMessage = (issueCodes) => {
    if (!issueCodes?.length) return "";
    const messages = issueCodes.map((code) => {
      if (code === "missing_required") {
        return t("settingsUsers.importErrorMissingRequired", {
          fields: t("Email address"),
        });
      }
      if (code === "duplicate_email") {
        return t("settingsUsers.importErrorDuplicateEmail");
      }
      if (code === "invalid_email") {
        return t("settingsUsers.importErrorInvalidEmail");
      }
      if (code === "missing_password") {
        return t("settingsUsers.importErrorMissingPassword");
      }
      if (code === "invalid_password") {
        return t("settingsUsers.importErrorInvalidPassword");
      }
      return code;
    });
    return [...new Set(messages)].join(" · ");
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    setIsLoadingUsers(true);

    getUsersList({ page: 1, limit: 10000 })
      .then((response) => {
        if (!cancelled) {
          setExistingUsers(response?.data?.data || []);
        }
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setExistingUsers([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingUsers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const validatedRows = useMemo(() => {
    const validated = validateImportRows(rows, existingUsers, {
      googleSSORequired,
    });
    if (!importReport) return validated;
    return mergeImportReport(validated, importReport);
  }, [rows, existingUsers, googleSSORequired, importReport]);

  const statusCounts = useMemo(
    () => countRowsByStatus(validatedRows),
    [validatedRows],
  );

  const statusByLine = useMemo(() => {
    const map = new Map();
    validatedRows.forEach((row) => map.set(row.line, row));
    return map;
  }, [validatedRows]);

  const readyCount = statusCounts[ROW_STATUS.READY] || 0;

  const handleCellChange = (line, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.line === line ? { ...row, [field]: value } : row)),
    );
  };

  const getStatusLabel = (row) => {
    switch (row.status) {
      case ROW_STATUS.READY:
        return t("settingsUsers.importStatusReady");
      case ROW_STATUS.INVALID:
        return t("settingsUsers.importStatusInvalid");
      case ROW_STATUS.SKIPPED:
        return t("settingsUsers.importStatusSkipped");
      case ROW_STATUS.CREATED:
        return t("settingsUsers.importStatusCreated");
      case ROW_STATUS.FAILED:
        return t("settingsUsers.importStatusFailed");
      default:
        return "";
    }
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const { rows: parsedRows } = await parseUserImportFile(file);
      setFileName(file.name);
      setRows(parsedRows);
      setImportDone(false);
      setImportReport(null);
    } catch (error) {
      console.error(error);
      toast.error(t("settingsUsers.importInvalidTemplate"));
      resetState();
    }
  };

  const handleImport = async () => {
    const toImport = getRowsToImport(validatedRows);
    if (!toImport.length) {
      toast.error(t("settingsUsers.importNoValidRows"));
      return;
    }

    setIsImporting(true);
    try {
      const response = await importUsers(
        toImport.map(({ status, issueCodes, ...row }) => row),
      );
      const payload = response?.data || {};
      setImportReport(payload);
      setImportDone(true);

      const hasIssues =
        (payload.failedCount || 0) > 0 || (payload.skippedCount || 0) > 0;

      if (hasIssues) {
        toast.warning(
          t("settingsUsers.importPartialSuccess", {
            created: payload.createdCount || 0,
            skipped: payload.skippedCount || 0,
            failed: payload.failedCount || 0,
          }),
        );
      } else {
        toast.success(
          t("settingsUsers.importSuccess", {
            total: payload.createdCount || 0,
          }),
        );
      }

      if ((payload.createdCount || 0) > 0) {
        onImported?.();
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.error || t("settingsUsers.importFailed"),
      );
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      style={{ maxWidth: "960px" }}
      contentClassName="rounded-md"
    >
      <ModalHeader className="pb-0 !border-b-0 px-6 pt-6" toggle={handleClose}>
        <h4 className="font-semibold text-[34px] leading-[42px] m-0">
          {t("settingsUsers.importModalTitle")}
        </h4>
      </ModalHeader>
      <ModalBody className="px-6 pt-4 pb-2">
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleFileSelected}
        />

        {!fileName ? (
          <>
            <label className="block text-sm font-semibold text-[#222222] mb-1">
              {t("settingsUsers.selectExcelFile")}
            </label>
            <p className="text-xs text-[#5e5e5e] mb-3 leading-[18px]">
              {googleSSORequired
                ? t("settingsUsers.importModalHelpSSO")
                : t("settingsUsers.importModalHelp")}
            </p>
          </>
        ) : null}

        <div className="flex items-center justify-between gap-2 mb-2 min-h-[40px]">
          {fileName ? (
            <p className="m-0 text-sm text-[#222222] truncate max-w-[640px]">
              {fileName}{" "}
              <span className="text-[#5e5e5e]">
                (
                {t("settingsUsers.importPreviewCount", {
                  total: validatedRows.length,
                })}
                )
              </span>
            </p>
          ) : (
            <button
              type="button"
              className="modal-button modal-button-cancel"
              onClick={() => inputRef.current?.click()}
            >
              {t("Upload file")}
            </button>
          )}
        </div>

        {isLoadingUsers && validatedRows.length > 0 ? (
          <p className="mb-3 text-xs text-[#5e5e5e]">
            {t("settingsUsers.importCheckingDuplicates")}
          </p>
        ) : null}

        {validatedRows.length > 0 ? (
          <ImportSummaryChips counts={statusCounts} t={t} />
        ) : null}

        {validatedRows.length > 0 ? (
          <div className="border border-[#d3d3d3] rounded-lg p-3 bg-white">
            <div className="overflow-x-auto overflow-y-auto max-h-[280px]">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_#f0f0f0]">
                  <tr className="text-left text-[#222222]">
                    <th className="pb-2 pr-2 font-semibold bg-white">
                      {t("Email address")}
                    </th>
                    <th className="pb-2 pr-2 font-semibold bg-white">
                      {t("First name")}
                    </th>
                    <th className="pb-2 pr-2 font-semibold bg-white">
                      {t("Name")}
                    </th>
                    <th className="pb-2 pr-2 font-semibold bg-white">
                      {t("Phone")}
                    </th>
                    {!googleSSORequired ? (
                      <th className="pb-2 pr-2 font-semibold bg-white">
                        {t("New password")}
                      </th>
                    ) : null}
                    <th className="pb-2 font-semibold bg-white text-right">
                      {t("settingsUsers.importColumnStatus")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((rawRow) => {
                    const row = statusByLine.get(rawRow.line) || rawRow;
                    const cellsDisabled = importDone || isImporting;
                    return (
                      <tr
                        key={rawRow.line}
                        className={`border-t border-[#f0f0f0] ${STATUS_ROW_CLASS[row.status] || ""}`}
                        title={resolveIssueMessage(row.issueCodes)}
                      >
                        <td className="py-1 pr-2 align-top">
                          <input
                            type="text"
                            value={rawRow.email ?? ""}
                            disabled={cellsDisabled}
                            placeholder={t("Email address")}
                            onChange={(e) =>
                              handleCellChange(
                                rawRow.line,
                                "email",
                                e.target.value.replace(/\s/g, "").toLowerCase(),
                              )
                            }
                            className={`w-full rounded border bg-transparent px-2 py-1 text-sm outline-none transition-colors focus:border-[#2AB9B8] focus:bg-white disabled:cursor-not-allowed disabled:hover:border-transparent ${
                              !row.email
                                ? "border-[#f5c6c6] text-[#c53030]"
                                : "border-transparent text-[#222222] hover:border-[#e0e0e0]"
                            }`}
                          />
                        </td>
                        <td className="py-1 pr-2 align-top">
                          <input
                            type="text"
                            value={rawRow.firstName ?? ""}
                            disabled={cellsDisabled}
                            placeholder={t("First name")}
                            onChange={(e) =>
                              handleCellChange(
                                rawRow.line,
                                "firstName",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-sm text-[#222222] outline-none transition-colors hover:border-[#e0e0e0] focus:border-[#2AB9B8] focus:bg-white disabled:cursor-not-allowed disabled:hover:border-transparent"
                          />
                        </td>
                        <td className="py-1 pr-2 align-top">
                          <input
                            type="text"
                            value={rawRow.lastName ?? ""}
                            disabled={cellsDisabled}
                            placeholder={t("Name")}
                            onChange={(e) =>
                              handleCellChange(
                                rawRow.line,
                                "lastName",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-sm text-[#222222] outline-none transition-colors hover:border-[#e0e0e0] focus:border-[#2AB9B8] focus:bg-white disabled:cursor-not-allowed disabled:hover:border-transparent"
                          />
                        </td>
                        <td className="py-1 pr-2 align-top">
                          <input
                            type="text"
                            value={rawRow.phone ?? ""}
                            disabled={cellsDisabled}
                            placeholder={t("Phone")}
                            onChange={(e) =>
                              handleCellChange(
                                rawRow.line,
                                "phone",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-sm text-[#222222] outline-none transition-colors hover:border-[#e0e0e0] focus:border-[#2AB9B8] focus:bg-white disabled:cursor-not-allowed disabled:hover:border-transparent"
                          />
                        </td>
                        {!googleSSORequired ? (
                          <td className="py-1 pr-2 align-top">
                            <input
                              type="text"
                              value={rawRow.password ?? ""}
                              disabled={cellsDisabled}
                              placeholder={t("New password")}
                              onChange={(e) =>
                                handleCellChange(
                                  rawRow.line,
                                  "password",
                                  e.target.value,
                                )
                              }
                              className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-sm text-[#222222] outline-none transition-colors hover:border-[#e0e0e0] focus:border-[#2AB9B8] focus:bg-white disabled:cursor-not-allowed disabled:hover:border-transparent"
                            />
                          </td>
                        ) : null}
                        <td className="py-1 text-right align-middle">
                          <span
                            className={`inline-block max-w-[100px] truncate rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4 ${STATUS_BADGE_CLASS[row.status] || ""}`}
                            title={resolveIssueMessage(row.issueCodes)}
                          >
                            {getStatusLabel(row)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {importDone ? (
          <p className="mt-3 mb-0 text-xs text-[#5e5e5e]">
            {t("settingsUsers.importDoneHint")}
          </p>
        ) : null}
      </ModalBody>
      <ModalFooter className="!border-t-0 px-6 pb-6 pt-2">
        <button
          type="button"
          className="modal-button modal-button-cancel mr-2"
          onClick={handleClose}
          disabled={isImporting}
        >
          {importDone ? t("Close") : t("Cancel")}
        </button>
        {!importDone ? (
          <button
            type="button"
            className="modal-button modal-button-save"
            onClick={handleImport}
            disabled={isImporting || readyCount === 0 || isLoadingUsers}
          >
            {readyCount > 0
              ? t("settingsUsers.importActionButtonWithCount", {
                  total: readyCount,
                })
              : t("settingsUsers.importActionButton")}
          </button>
        ) : (
          <button
            type="button"
            className="modal-button modal-button-save"
            onClick={() => {
              resetState();
              inputRef.current?.click();
            }}
          >
            {t("settingsUsers.importAnotherFile")}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ImportUsersModal;
